/* eslint-disable max-len */
import React, { useState } from 'react';
import {
    IContextualMenuItem,
    IContextualMenuProps,
    IconButton,
    Dialog,
    DialogType,
    // Spinner,
    // SpinnerSize,
    // Checkbox,
} from '@fluentui/react';
import { appStyles } from '../../../helpers';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
    openDownloadLinkThunk,
    deleteFileByPathThunk,
    openFileInNextcloudThunk,
    isOpenableLocally,
    openFileLocallyThunk,
    openFileInDesktopClientThunk,
} from '../../../store/actions/app/thunks';
import { removeDefaultFolderThunk } from '../../../store/actions/defaultFolder/thunks';
import { ConfirmDialog } from '../../common/confirmDialog';
import { FileType } from '../../../model';
import { EditFilenameForm } from '../../form/editFilenameForm';
import { selectTranslate } from '../../../store/selectors/ui';
import { ChatMemberRole } from '../../../services/microsoft/msGraphApi';
import { selectTeamMember } from '../../../store/selectors/conversation';
import {
    selectDefaultFolderId,
    selectDefaultFolderModalStep,
} from '../../../store/selectors/defaultFolder';
import { selectTeamsOpenFileMode } from '../../../store/selectors/auth';
import { DefaultFolderModalStep } from '../../../store/reducers/defaultFolder/reducer';
import { ConfirmationModal } from './defaultFolderModals/confirmationModal';
import { CircleSelectionModal } from './defaultFolderModals/circleSelectionModal';
import { SetFolderModal } from './defaultFolderModals/setFolderModal';
import {
    setDefaultFolderModalStep,
    setFormDefaultFolderId,
    setFormDefaultFolderPath,
} from '../../../store/actions/defaultFolder/actionCreators';
import { batch } from 'react-redux';

type FileDropdownButtonProps = {
    filePath: string;
    fileId: number | undefined;
    type: FileType;
};

export const DefaultFolderModal = () => {
    const defaultFolderModalStep = useAppSelector(selectDefaultFolderModalStep);

    switch (defaultFolderModalStep) {
        case DefaultFolderModalStep.SetFolder:
            return <SetFolderModal />;
        case DefaultFolderModalStep.CircleSelection:
            return <CircleSelectionModal />;
        case DefaultFolderModalStep.Confirmation:
            return <ConfirmationModal />;
        default:
            return null;
    }
};

const renderNothing = (): null => null;

export const FileDropdownButton = ({
    filePath,
    fileId,
    type,
}: FileDropdownButtonProps): JSX.Element => {
    const dispatch = useAppDispatch();
    const translate = useAppSelector(selectTranslate);
    const teamMember = useAppSelector(selectTeamMember);
    const defaultFolderId = useAppSelector(selectDefaultFolderId);
    const openFileMode = useAppSelector(selectTeamsOpenFileMode);
    const isOwner = teamMember?.roles?.includes(ChatMemberRole.Owner);

    const onDownloadClick = () => {
        dispatch(openDownloadLinkThunk(filePath));
    };

    const [isEditHidden, setIsEditHidden] = useState(true);
    const [isDeleteConfirmHidden, setIsDeleteConfirmHidden] = useState(true);

    const onDeleteConfirmClick: (
        ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        item?: IContextualMenuItem,
    ) => boolean | void = () => {
        closeConfirmDeleteDialog();
        dispatch(deleteFileByPathThunk(filePath));
    };

    // Base items (always shown)
    const items: IContextualMenuItem[] = [
        {
            key: 'editFile',
            text: translate('rename'),
            iconProps: { iconName: 'Edit' },
            onClick: () => setIsEditHidden(false),
        },
        {
            key: 'deleteFile',
            text: translate('delete'),
            iconProps: { iconName: 'Delete' },
            onClick: () => setIsDeleteConfirmHidden(false),
        },
    ];

    if (type !== 'directory') {
        // File-only items
        // TODO: download is hidden for now (disabled + renderNothing). Remove?
        items.push({
            key: 'downloadFile',
            text: translate('download'),
            disabled: true,
            onRender: renderNothing,
            iconProps: { iconName: 'Download' },
            onClick: onDownloadClick,
        });

        items.push({
            key: 'openFileInNextcloud',
            text: translate('openFileInNextcloud'),
            iconProps: { iconName: 'OpenInNewWindow' },
            onClick: () => dispatch(openFileInNextcloudThunk(fileId)),
        });

        if (openFileMode === 'desktop') {
            items.push({
                key: 'openInDesktopClient',
                text: translate('openInDesktopClient'),
                iconProps: { iconName: 'CloudDownload' },
                onClick: () => dispatch(openFileInDesktopClientThunk(filePath)),
            });
        }

        if (openFileMode === 'webdav' && isOpenableLocally(filePath)) {
            items.push({
                key: 'openFileLocally',
                text: translate('openFileLocally'),
                iconProps: { iconName: 'OpenFile' },
                onClick: () => dispatch(openFileLocallyThunk(filePath)),
            });
        }
    }

    // Directory-only items (owner)
    if (isOwner && type === 'directory') {
        if (defaultFolderId == fileId) {
            items.push({
                key: 'removeDefaultFolder',
                text: translate('remove_default_folder'),
                iconProps: { iconName: 'Cancel' },
                onClick: () => dispatch(removeDefaultFolderThunk()),
            });
        } else {
            items.push({
                key: 'setDefaultFolder',
                text: translate('set_default_folder'),
                iconProps: { iconName: 'FavoriteStar' },
                onClick: () => {
                    batch(() => {
                        dispatch(setFormDefaultFolderId(fileId ?? null));
                        dispatch(setFormDefaultFolderPath(filePath));
                        dispatch(
                            setDefaultFolderModalStep(
                                DefaultFolderModalStep.SetFolder,
                            ),
                        );
                    });
                },
            });
        }
    }

    const menuProps: IContextualMenuProps = {
        items,
        directionalHintFixed: true,
    };

    const closeEditDialog = () => setIsEditHidden(true);
    const closeConfirmDeleteDialog = () => setIsDeleteConfirmHidden(true);

    return (
        <>
            <IconButton
                styles={appStyles.menuIconButton}
                menuProps={menuProps}
                onRenderMenuIcon={renderNothing}
                iconProps={{ iconName: 'More' }}
            />
            <Dialog
                styles={appStyles.DialogStyle}
                hidden={isEditHidden}
                onDismiss={closeEditDialog}
                dialogContentProps={{ type: DialogType.close }}
            >
                <EditFilenameForm
                    initialPath={filePath}
                    onCancel={closeEditDialog}
                />
            </Dialog>
            <ConfirmDialog
                text={translate('confirm_delete_item')}
                hidden={isDeleteConfirmHidden}
                onConfirm={onDeleteConfirmClick}
                onDismiss={closeConfirmDeleteDialog}
                onClose={closeConfirmDeleteDialog}
            />
        </>
    );
};
